import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Mail, Phone, MessageSquare, Calendar, FileText, HelpCircle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useState } from "react";
import ContactFormModal from "@/components/ContactFormModal";
import CalendarBookingModal from "@/components/CalendarBookingModal";

export default function Support() {
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const contactMethods = [
    {
      icon: Mail,
      title: "Email Us",
      description: "Send us a message and we'll respond within 24 hours",
      action: "Get in Touch",
      onClick: () => setIsContactOpen(true),
    },
    {
      icon: Calendar,
      title: "Schedule a Call",
      description: "Book a time that works for you to discuss your needs",
      action: "Book Now",
      onClick: () => setIsCalendarOpen(true),
    },
    {
      icon: Phone,
      title: "Call Us",
      description: "Prefer to talk? Give us a call during business hours",
      action: "Coming Soon",
      onClick: () => {},
      disabled: true,
    },
  ];

  const resources = [
    {
      icon: FileText,
      title: "Documentation",
      description: "Access guides and tutorials for our solutions",
      status: "Coming Soon"
    },
    {
      icon: HelpCircle,
      title: "FAQ",
      description: "Find answers to commonly asked questions",
      link: "/faq"
    },
    {
      icon: MessageSquare,
      title: "Case Studies",
      description: "See how we've helped businesses like yours",
      link: "/case-studies"
    },
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/20 to-background" />
          
          <div className="container relative z-10 max-w-4xl mx-auto text-center">
            <motion.h1
              className="text-5xl md:text-6xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              We're Here to <span className="text-gradient">Help</span>
            </motion.h1>
            
            <motion.p
              className="text-xl text-muted-foreground mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Whether you're a current client or exploring our services, we're ready to answer your questions and support your success.
            </motion.p>
          </div>
        </section>

        {/* Contact Methods */}
        <section className="py-20 px-4">
          <div className="container max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Get in Touch
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {contactMethods.map((method, index) => (
                <motion.div
                  key={method.title}
                  className="p-6 rounded-lg bg-card border border-border text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                    <method.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{method.title}</h3>
                  <p className="text-muted-foreground mb-6">{method.description}</p>
                  <Button
                    onClick={method.onClick}
                    disabled={method.disabled}
                    className="w-full"
                    variant={method.disabled ? "outline" : "default"}
                  >
                    {method.action}
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Support Hours */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="container max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Support Hours
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <motion.div
                className="p-6 rounded-lg bg-card border border-border"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-semibold mb-4">General Inquiries</h3>
                <div className="space-y-2 text-muted-foreground">
                  <p>Monday - Friday: 9:00 AM - 6:00 PM EST</p>
                  <p>Saturday - Sunday: Closed</p>
                  <p className="text-sm mt-4">Email responses within 24 hours</p>
                </div>
              </motion.div>
              
              <motion.div
                className="p-6 rounded-lg bg-card border border-border"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <h3 className="text-xl font-semibold mb-4">Client Support</h3>
                <div className="space-y-2 text-muted-foreground">
                  <p>Priority support for active projects</p>
                  <p>Emergency support available for critical issues</p>
                  <p className="text-sm mt-4">Response time: 2-4 hours during business hours</p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Resources */}
        <section className="py-20 px-4">
          <div className="container max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Helpful Resources
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {resources.map((resource, index) => (
                <motion.div
                  key={resource.title}
                  className="p-6 rounded-lg bg-card border border-border hover:border-primary/50 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <resource.icon className="w-12 h-12 text-primary mb-4" />
                  <h3 className="text-xl font-semibold mb-3">{resource.title}</h3>
                  <p className="text-muted-foreground mb-4">{resource.description}</p>
                  {resource.link ? (
                    <a href={resource.link} className="text-primary hover:underline">
                      Learn More →
                    </a>
                  ) : (
                    <span className="text-muted-foreground text-sm">{resource.status}</span>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* What to Expect */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="container max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              What to Expect
            </h2>
            
            <div className="space-y-6">
              {[
                {
                  title: "Fast Response Times",
                  description: "We respond to all inquiries within 24 hours, usually much faster. For active clients, expect responses within 2-4 hours during business hours."
                },
                {
                  title: "Clear Communication",
                  description: "We explain technical concepts in plain English and keep you informed every step of the way. No jargon, no confusion."
                },
                {
                  title: "Proactive Support",
                  description: "We don't just fix problems—we monitor your systems, suggest improvements, and help you get the most value from your investment."
                },
                {
                  title: "Long-Term Partnership",
                  description: "We're not just here for the initial project. We're committed to your ongoing success and available whenever you need us."
                },
              ].map((item, index) => (
                <motion.div
                  key={item.title}
                  className="p-6 rounded-lg bg-card border border-border"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4">
          <div className="container max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Start?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Whether you have a question or you're ready to begin a project, we'd love to hear from you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => setIsContactOpen(true)}
                className="bg-gradient-to-r from-primary to-secondary text-white"
              >
                Send a Message
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => setIsCalendarOpen(true)}
              >
                Schedule a Call
              </Button>
            </div>
          </div>
        </section>
      </div>
      <Footer />
      <ContactFormModal open={isContactOpen} onOpenChange={setIsContactOpen} />
      <CalendarBookingModal isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} />
    </>
  );
}
